import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentInternalUser, isAdmin } from '../lib/currentUser';
import HowItWorksButton from '../components/HowItWorksButton';
import Sidebar from '../components/Sidebar';
import FusionDocuments from '../components/FusionDocuments';
import RiskFlagMatrix from '../components/RiskFlagMatrix';

// Documents page — the configure-once half of what used to be the single
// Compliance page. Holds the master document library (Fusion Documents) and
// the risk-tier → required-document matrix (Risk Flag Configuration).
//
// The operational half — Review Queue + Compliance Overview — moved to the
// Main Menu "Compliance" item (/compliance, AdminCompliance). Splitting them
// keeps the daily review work in the Main Menu and the setup tools here in the
// System group.

const SECTIONS = [
  { key: 'documents', label: 'Fusion Documents' },
  { key: 'risk',      label: 'Risk Flag Configuration' },
];

export default function ComplianceDocuments() {
  const navigate = useNavigate();
  const { user: currentInternalUser } = useCurrentInternalUser();
  const [section, setSection] = useState('documents');

  // Admin / super-user gate. The sidebar hides this surface for non-admins,
  // but a direct URL hit needs the page itself to refuse.
  if (currentInternalUser && !isAdmin(currentInternalUser)) {
    return (
      <div className="min-h-screen bg-canvas flex">
        <Sidebar currentInternalUser={currentInternalUser} />
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <p className="font-['Metropolis'] text-[10px] font-bold tracking-[3px] uppercase text-[#7A4E10] mb-3">Restricted</p>
          <p className="font-['Metropolis'] text-lg text-ink mb-6 max-w-md">Documents is restricted to Admin and Super User accounts.</p>
          <button onClick={() => navigate('/dashboard')} className="font-['Metropolis'] text-xs font-semibold tracking-[2px] uppercase text-fusion-forest border border-[#92D400] rounded-full px-5 py-2 hover:bg-active-green/10 transition-colors">
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas flex">
      <Sidebar currentInternalUser={currentInternalUser} />

      <div className="flex-1 flex flex-col">
        <div className="pt-8">
          <div className="px-8 mb-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="fusion-h1 text-fusion-forest">Documents</h1>
              <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-2">
                Manage the master document library and configure which documents each risk tier must hold.
              </p>
            </div>
            {/* Shared component since 2026-08-14 — Suppliers, Briefs and
                Compliance each have one now, and four hand-rolled copies of
                one pill is four chances for them to drift. */}
            <HowItWorksButton to="/documents/how-it-works" />
          </div>
          <div className="mx-8 h-px bg-[#E6E9DA]" aria-hidden="true" />
        </div>

        {/* Section tabs — Fusion Documents / Risk Flag Configuration. */}
        <div className="mx-8 mt-6">
          <div className="flex items-end gap-1 border-b border-line flex-wrap">
            {SECTIONS.map(s => {
              const isActive = section === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSection(s.key)}
                  className={`font-['Metropolis'] text-sm font-semibold px-5 py-3 transition-colors ${
                    isActive
                      ? 'text-fusion-forest border-b-2 border-[#92D400] -mb-px'
                      : 'text-[#51534A] hover:text-fusion-mid-grey border-b-2 border-transparent'
                  }`}>
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        {section === 'documents' && (
          <div className="px-8 pb-12 mt-6">
            <FusionDocuments currentUser={currentInternalUser} onOpenRiskFlagConfig={() => setSection('risk')} />
          </div>
        )}

        {section === 'risk' && (
          <div className="px-8 pb-12 mt-6">
            <RiskFlagMatrix currentUser={currentInternalUser} />
          </div>
        )}
      </div>
    </div>
  );
}
